/**
 * Types pour le système de table brouillon (draft) - Frontend
 */

// ============================================
// Positions autour de la table
// ============================================

export type ClassicSeatPosition = 'sud' | 'est' | 'nord' | 'ouest' | 'nord_est' | 'nord_ouest';
export type CompetSeatPosition = 'attaquant' | 'def_E' | 'def_N' | 'def_O';
export type SeatPosition = ClassicSeatPosition | CompetSeatPosition;

/**
 * Labels des positions.
 * `hideRoleLabels` : en compétition, n'affiche que la localisation (Nord/Est/Sud/Ouest)
 * sans le rôle attaquant/défenseur — utilisé quand "Attaquant tournant" est activé,
 * puisque le rôle change alors à chaque donne (voir `forceMinDonnes` / `attaquantTournant`).
 */
export function getSeatLabel(seat: SeatPosition, compet: boolean, hideRoleLabels = false): string {
  if (compet) {
    if (hideRoleLabels) {
      const locationLabels: Record<string, string> = {
        attaquant: 'Sud',
        def_E: 'Est',
        def_N: 'Nord',
        def_O: 'Ouest',
      };
      return locationLabels[seat] || seat;
    }
    const labels: Record<string, string> = {
      attaquant: 'Attaquant (Sud)',
      def_E: 'Défenseur Est (fond)',
      def_N: 'Défenseur Nord (milieu)',
      def_O: 'Défenseur Ouest (devant)',
    };
    return labels[seat] || seat;
  }
  const labels: Record<string, string> = {
    sud: 'Sud',
    est: 'Est',
    nord: 'Nord',
    ouest: 'Ouest',
    nord_est: 'Nord-Est',
    nord_ouest: 'Nord-Ouest',
  };
  return labels[seat] || seat;
}

/** Positions actives pour un nombre de joueurs donné */
export function getActiveSeats(nbJoueurs: 3 | 4 | 5, compet: boolean): SeatPosition[] {
  if (compet && nbJoueurs === 4) {
    return ['attaquant', 'def_E', 'def_N', 'def_O'];
  }
  switch (nbJoueurs) {
    case 3: return ['sud', 'est', 'ouest'];
    case 4: return ['sud', 'est', 'nord', 'ouest'];
    case 5: return ['sud', 'est', 'nord_est', 'nord_ouest', 'ouest'];
  }
}

/** Indices de siège occupés par des humains (draft legacy). */
export function getHumanSeatIndicesFromPlayers(
  players: DraftPlayer[],
  nbJoueurs: 3 | 4 | 5,
  compet: boolean,
): number[] {
  const activeSeats = getActiveSeats(nbJoueurs, compet);
  const indices: number[] = [];
  for (const player of players) {
    if (player.status !== 'joined' || !player.seat) continue;
    const idx = activeSeats.indexOf(player.seat);
    if (idx >= 0) indices.push(idx);
  }
  return [...new Set(indices)].sort((a, b) => a - b);
}

/** Bloc contigu sur la table ronde (wrap autorisé). */
export function areHumanSeatsContiguousOnCircle(humanIndices: number[], totalSeats: number): boolean {
  if (humanIndices.length <= 1) return true;
  const sorted = [...humanIndices].sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const curr = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    gaps.push(next > curr ? next - curr - 1 : next + totalSeats - curr - 1);
  }
  const botCount = totalSeats - sorted.length;
  return Math.max(...gaps) === botCount;
}

/** Sièges 0..k−1 sans trou (requis pour entrée libre Java). */
export function areHumanSeatsLibreSequential(humanIndices: number[]): boolean {
  const sorted = [...humanIndices].sort((a, b) => a - b);
  return sorted.every((idx, i) => idx === i);
}

/** Au moins un humain connecté occupe le Sud (siège 0). */
export function hasHumanInSouthSeat(
  players: DraftPlayer[],
  nbJoueurs: 3 | 4 | 5,
  compet: boolean,
): boolean {
  const southSeat = getActiveSeats(nbJoueurs, compet)[0];
  return players.some((p) => p.status === 'joined' && p.seat === southSeat);
}

function isDraftPlayerSeated(
  player: DraftPlayer,
  dualTable: boolean,
): boolean {
  if (!player.seat) return false;
  if (dualTable && player.tableIndex === undefined) return false;
  return true;
}

/** Erreurs bloquant le lancement de la partie (miroir de validateForFinalization côté serveur). */
export function getDraftFinalizationError(draft: DraftTable): string | null {
  const { config, players, compet } = draft;
  const joined = players.filter((p) => p.status === 'joined');
  const joinedCount = joined.length;
  const dualTable = config.gameMode === 'defi_libre' && config.nbTables === 2;

  if (config.gameMode === 'libre') {
    if (joinedCount > config.nombreJoueurs) {
      return `Trop de joueurs (${joinedCount}/${config.nombreJoueurs})`;
    }
    if (config.tableType === 'neo' && config.nombreJoueurs !== 4) {
      return 'Les tables Neo ne supportent que 4 joueurs (IA disponible uniquement à 4).';
    }
  } else {
    const max = config.nbTables * 4;
    if (joinedCount > max) {
      return `Trop de joueurs (${joinedCount}/${max})`;
    }
    if (joinedCount < 2) {
      return 'Au moins 2 joueurs requis pour un défi libre';
    }
  }

  const seatedHumans = joined.filter((p) => isDraftPlayerSeated(p, dualTable));
  if (seatedHumans.length === 0) {
    return 'Au moins un joueur humain doit être placé à la table';
  }

  if (config.tableType === 'neo') {
    const unplaced = joined.filter((p) => !isDraftPlayerSeated(p, dualTable));
    if (unplaced.length > 0 && !config.spectateurTempsReel) {
      return (
        'Des joueurs non placés deviendraient spectateurs sans accès en direct. ' +
        'Placez-les à la table ou activez « Spectateurs en temps réel ».'
      );
    }
  }

  if (config.tableType === 'legacy' && config.gameMode === 'libre') {
    for (const player of joined) {
      if (!player.seat) {
        return `${player.pseudoDisplay || player.pseudo} n'a pas de place assignée`;
      }
    }

    if (!hasHumanInSouthSeat(players, config.nombreJoueurs, compet)) {
      if (config.modeEntree === 2) {
        return 'Une table classique requiert qu\'un humain soit placé en Sud (le créateur y sera connecté)';
      }
      return 'Une table classique dont l\'entrée n\'est pas filtrée requiert qu\'un humain soit placé en Sud';
    }

    if (config.modeEntree !== 2) {
      const humanIndices = getHumanSeatIndicesFromPlayers(
        players,
        config.nombreJoueurs,
        compet,
      );

      if (!areHumanSeatsContiguousOnCircle(humanIndices, config.nombreJoueurs)) {
        return (
          'Les joueurs humains doivent être placés les uns à côté des autres, ' +
          'sans bot intercalé entre deux humains'
        );
      }

      if (!areHumanSeatsLibreSequential(humanIndices)) {
        return (
          'En entrée libre, les humains doivent occuper les premières places à partir du Sud, sans trou. ' +
          'Utilisez « Entrée filtrée » pour un placement libre.'
        );
      }
    }
  }

  return null;
}

// ============================================
// Joueurs
// ============================================

export type DraftPlayerStatus = 'joined' | 'invited' | 'declined' | 'waiting_game' | 'waiting_connect';

export interface DraftPlayer {
  pseudo: string;
  pseudoDisplay: string;
  status: DraftPlayerStatus;
  isAdmin: boolean;
  seat?: SeatPosition;
  tableIndex?: number;
  joinedAt: number;
}

// ============================================
// Configuration
// ============================================

export type DraftTableType = 'legacy' | 'neo';
export type DraftGameMode = 'libre' | 'defi_libre';
export type DraftAIType = 'delphi' | 'rv' | 'ml';
export type DraftEntryMode = 1 | 2 | 3;

/** Valeur `nbDonnes` pour les tables Neo : parties enchaînées sans limite. */
export const NB_DONNES_UNLIMITED = 0;

export interface DraftTableConfig {
  tableType: DraftTableType;
  gameMode: DraftGameMode;
  nombreJoueurs: 3 | 4 | 5;
  commentaire: string;
  ia: DraftAIType;
  modeEntree: DraftEntryMode;
  motDePasse?: string;
  petitesAutorisees: boolean;
  iaPeutPrendre: boolean;
  tableCachee: boolean;
  /** Nombre de donnes (Neo : 0 = sans limite). */
  nbDonnes: number;
  /**
   * Attaquant tournant : selon le mode, cette option a deux usages distincts (jamais actifs
   * simultanément, les modes étant exclusifs) :
   * - `defi_libre` (legacy) : le capitaine d'équipe change à chaque défi.
   * - `libre` + `tableType === 'neo'` : le joueur ciblé par `forceMinDonnes` change à chaque
   *   donne (voir plus bas). Ne peut être activé que si `forceMinDonnes` l'est aussi.
   */
  attaquantTournant: boolean;
  surcontratAutorise: boolean;
  messageriesInterdites: boolean;
  defiOuvert: boolean;
  nbTables: 1 | 2;
  spectateursAutorises?: boolean;
  rejoindreEnCours?: boolean;
  spectateurTempsReel?: boolean;
  /**
   * Force une force minimum (barème Chavey) pour la donne du joueur "attaquant" (position
   * fixe, ou tournante si `attaquantTournant` est activé). Neo table libre uniquement.
   */
  forceMinDonnes: boolean;
  /** Seuil minimal exigé : 0=hasard pur, 1=petite, 2=garde, 3=garde sans, 4=garde contre. */
  forceMinDonnesSeuil: 0 | 1 | 2 | 3 | 4;
  /** Personnalisation fine du score Chavey minimal (entre les crans classiques). */
  forceMinDonnesCustom?: boolean;
  /** Score Chavey minimal (0–100) ; prioritaire sur le seuil discret si personnalisé. */
  forceMinDonnesMinScore?: number;
}

export const DEFAULT_DRAFT_CONFIG: DraftTableConfig = {
  tableType: 'legacy',
  gameMode: 'libre',
  nombreJoueurs: 4,
  commentaire: '',
  ia: 'delphi',
  modeEntree: 1,
  petitesAutorisees: true,
  iaPeutPrendre: true,
  tableCachee: false,
  nbDonnes: 4,
  attaquantTournant: false,
  surcontratAutorise: false,
  messageriesInterdites: false,
  defiOuvert: false,
  nbTables: 1,
  spectateursAutorises: true,
  rejoindreEnCours: false,
  spectateurTempsReel: false,
  forceMinDonnes: true,
  forceMinDonnesSeuil: 2,
  forceMinDonnesCustom: false,
};

// ============================================
// Messages
// ============================================

export interface DraftMessage {
  id: string;
  pseudo: string;
  pseudoDisplay: string;
  text: string;
  timestamp: number;
  isSystem: boolean;
}

// ============================================
// Table brouillon
// ============================================

export type DraftStatus = 'configuring' | 'ready' | 'creating' | 'created' | 'error';

export interface DraftTable {
  id: string;
  createdBy: string;
  compet: boolean;
  config: DraftTableConfig;
  players: DraftPlayer[];
  messages: DraftMessage[];
  status: DraftStatus;
  createdAt: number;
  updatedAt: number;
  finalizedTable?: {
    ip: string;
    port: number;
    connectionId?: string;
    serverConnectionId?: string;
  };
  errorMessage?: string;
}

// ============================================
// Profil de disponibilité
// ============================================

export type AvailabilityProfile = 'bougon' | 'timide' | 'detendu' | 'enthousiaste' | 'on_fire';

export const AVAILABILITY_LABELS: Record<AvailabilityProfile, { label: string; emoji: string; description: string }> = {
  bougon: {
    label: 'Bougon',
    emoji: '😤',
    description: 'Invitations masquées et refusées systématiquement',
  },
  timide: {
    label: 'Timide',
    emoji: '😊',
    description: 'Invitations affichées, refusées sans réponse',
  },
  detendu: {
    label: 'Détendu',
    emoji: '😎',
    description: 'Invitations affichées, acceptées sans réponse',
  },
  enthousiaste: {
    label: 'Enthousiaste',
    emoji: '🤩',
    description: 'Invitations toujours acceptées',
  },
  on_fire: {
    label: 'On fire !',
    emoji: '🔥',
    description: 'Toujours acceptées + statut "prêt à jouer"',
  },
};

export type CompetRolePreference = 'none' | 'defender' | 'attacker';

export interface AvailabilityOptions {
  acceptIfFriend?: boolean;
  acceptIfTeammate?: boolean;
  acceptedPlayerCounts?: PlayerCountMode[];
  competRolePreference?: CompetRolePreference;
  /** @deprecated */
  maxPlayers?: number;
  friends?: string[];
  ignored?: string[];
}

export type PlayerCountMode = 3 | 4 | 5;

export const COMPET_ROLE_PREFERENCES: CompetRolePreference[] = ['none', 'defender', 'attacker'];

export const COMPET_ROLE_LABELS: Record<
  CompetRolePreference,
  { label: string; short: string; description: string }
> = {
  none: {
    label: 'Pas d\'exigence',
    short: '',
    description: 'Accepte de jouer attaquant ou défenseur selon le placement à la table.',
  },
  defender: {
    label: 'Uniquement défenseur',
    short: 'Déf.',
    description: 'Souhaite jouer uniquement en défense (pas preneur).',
  },
  attacker: {
    label: 'Uniquement attaquant',
    short: 'Att.',
    description: 'Souhaite jouer uniquement en attaque (preneur).',
  },
};

export const DEFAULT_ACCEPTED_PLAYER_COUNTS: PlayerCountMode[] = [3, 4, 5];

export interface SocialListEntry {
  pseudo: string;
  pseudoDisplay: string;
}

export interface SocialProfileData {
  friends: SocialListEntry[];
  ignored: SocialListEntry[];
  teammates: Array<SocialListEntry & { teamName?: string }>;
  acceptIfFriend: boolean;
  acceptIfTeammate: boolean;
  /** Formats de table acceptés en détente (3 / 4 / 5 joueurs) */
  acceptedPlayerCounts: PlayerCountMode[];
  /** Exigence attaquant / défenseur en compétition */
  competRolePreference: CompetRolePreference;
}
